import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  type FC,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { apolloClient } from '../api/apolloClient';
import { REFRESH_AUTH_TOKEN_MUTATION } from '../graphql/resolvers/userMutations/refreshAuthToken.gql';
import { getTokenFromLocalStorage } from '../helpers/getTokenFromLocalStorage';
import { CoreUserDDB } from '../interfaces/user.interface';

interface AuthContextProps {
  isSignedIn: boolean;
  isLoading: boolean;
  session: string;
  isSignedInRef: MutableRefObject<boolean>;
  // TRIL-231: true between the start of an explicit logout and the next login,
  // so App.tsx's private-route guard can tell a deliberate logout apart from an
  // unauthenticated deep-link bounce and skip capturing a spurious `from`.
  loggingOutRef: MutableRefObject<boolean>;
  user: CoreUserDDB | PartialUserLoginPayloadDDB | null;
  login: (loginResponse: any) => void;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<CoreUserDDB>) => void;
  // TRIL-221: returns true if refresh succeeded (new tokens written +
  // isSignedIn flipped to true), false otherwise (logout already fired).
  refreshToken: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps,
);

interface AuthProviderProps {
  children: ReactNode;
}

type PartialUserLoginPayloadDDB = Pick<
  CoreUserDDB,
  'email' | 'username' | 'newUser' | 'id'
>;

const decodeUserFromToken = (
  token: string,
): PartialUserLoginPayloadDDB | null => {
  try {
    const decoded = jwtDecode(token) as any;
    if (!decoded || !decoded.userId) {
      return null;
    }
    return {
      newUser: undefined,
      id: decoded.userId,
      username: '',
      email: '',
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

const setTokenInLocalStorage = (key: string, token: string) => {
  try {
    localStorage.setItem(key, token);
  } catch (error) {
    console.error('Error setting token in localStorage:', error);
  }
};

const setUserInLocalStorage = (user: PartialUserLoginPayloadDDB) => {
  try {
    localStorage.setItem('userData', JSON.stringify(user));
  } catch (error) {
    console.error('Error setting user in localStorage:', error);
  }
};

const getUserFromLocalStorage = (): CoreUserDDB | null => {
  try {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting user from localStorage:', error);
    return null;
  }
};

const clearAuthFromLocalStorage = () => {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userData');
  } catch (error) {
    console.error('Error clearing auth from localStorage:', error);
  }
};

// Synchronously hydrate auth state from localStorage at mount time. Reading
// here (rather than in an async useEffect) eliminates a race where the first
// render sees isSignedIn=false and any route guard that checks it redirects
// authenticated users to /log-in before the async rehydration commits.
type InitialAuthState = {
  isSignedIn: boolean;
  session: string;
  user: PartialUserLoginPayloadDDB | CoreUserDDB | null;
  // TRIL-221: true when the access token is expired but a refresh token
  // exists in localStorage. The AuthProvider's mount-time useEffect picks
  // this up to attempt silent refresh before any route guards run.
  needsRefresh: boolean;
};

const readInitialAuthState = (): InitialAuthState => {
  if (typeof window === 'undefined') {
    return { isSignedIn: false, session: '', user: null, needsRefresh: false };
  }
  const token = window.localStorage.getItem('accessToken');
  if (!token) {
    return { isSignedIn: false, session: '', user: null, needsRefresh: false };
  }
  try {
    const decoded = jwtDecode(token) as { exp?: number; userId?: string };
    if (!decoded?.exp || decoded.exp <= Date.now() / 1000) {
      // TRIL-221: access token expired. If a refresh token exists in
      // localStorage, signal that the mount-time effect should attempt
      // silent refresh before falling through to the signed-out path.
      const refreshTokenValue = window.localStorage.getItem('refreshToken');
      return {
        isSignedIn: false,
        session: '',
        user: null,
        needsRefresh: Boolean(refreshTokenValue),
      };
    }
    const userData = getUserFromLocalStorage();
    // Trust persisted userData only when its id matches the JWT's userId
    // claim. The token is the cryptographic source of truth — the server
    // validates it on every request. If the two disagree (partial state
    // after a refresh-token swap, cross-tab account change, etc.), the
    // token wins; the persisted blob is just a UX cache.
    const tokenMatchesPersisted =
      !!userData?.id && !!decoded.userId && userData.id === decoded.userId;
    const hydratedUser = tokenMatchesPersisted
      ? userData
      : decodeUserFromToken(token);
    if (!hydratedUser) {
      return {
        isSignedIn: false,
        session: '',
        user: null,
        needsRefresh: false,
      };
    }
    return {
      isSignedIn: true,
      session: token,
      user: hydratedUser,
      needsRefresh: false,
    };
  } catch {
    return { isSignedIn: false, session: '', user: null, needsRefresh: false };
  }
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSignedIn, setIsSignedIn] = useState<boolean>(
    () => readInitialAuthState().isSignedIn,
  );
  // TRIL-221: when readInitialAuthState detects an expired access token +
  // a valid refresh token in localStorage, init isLoading=true so the
  // App.tsx renderRoutes shows <SimpleBackdrop> while the mount-time
  // useEffect (below) attempts silent refresh. Before TRIL-221 this was
  // always false (the App.tsx isLoading branch was dead code per TRIL-217).
  const [isLoading, setIsLoading] = useState<boolean>(
    () => readInitialAuthState().needsRefresh,
  );
  const [session, setSession] = useState<string>(
    () => readInitialAuthState().session,
  );
  const [user, setUser] = useState<
    PartialUserLoginPayloadDDB | CoreUserDDB | null
  >(() => readInitialAuthState().user);

  const isSignedInRef = useRef(false);
  isSignedInRef.current = isSignedIn;

  // TRIL-231: set true synchronously at the start of logout() — before the
  // setIsSignedIn(false) re-render that re-runs the route guard — and cleared
  // on the next login(). See resolveGuardRedirectState.
  const loggingOutRef = useRef(false);

  // Self-heal stale userData on mount. If the token's userId disagreed with
  // the persisted userData.id, readInitialAuthState() resolved as the
  // token-decoded user — but localStorage still carries the stale blob.
  // Rewrite it once so the next refresh starts clean and a future engineer
  // doesn't see a perpetual mismatch-fallback in the logs.
  useEffect(() => {
    if (!user?.id) return;
    const persisted = getUserFromLocalStorage();
    if (persisted?.id !== user.id) {
      setUserInLocalStorage(user as PartialUserLoginPayloadDDB);
    }
    // Mount-only: the rest of the lifecycle (login, updateUser) maintains
    // userData on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((loginResponse: any) => {
    // TRIL-231: re-enable deep-link `from` capture for any future
    // unauthenticated bounce. Unconditional and first — mirrors how logout()
    // sets the ref — so a malformed login response can't leave it stale.
    loggingOutRef.current = false;

    const { accessToken, refreshToken, sessionId, ...userData } = loginResponse;

    if (accessToken && userData.id) {
      setTokenInLocalStorage('accessToken', accessToken);
      if (refreshToken) {
        setTokenInLocalStorage('refreshToken', refreshToken);
      }
      // TRIL-377: remember this session's id so the Devices list can flag
      // "This device" (compared client-side; never a server role check).
      if (sessionId) {
        setTokenInLocalStorage('sessionId', sessionId);
      }

      const userObj: CoreUserDDB = {
        id: userData.id,
        username: userData.username,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        avatar: userData.avatar,
        bio: userData.bio,
        interests: userData.interests || [],
        role: userData.role,
        country: userData.country,
        active: userData.active,
        lastActive: userData.lastActive,
        createdAt: userData.createdAt,
        newUser: userData.newUser,
        reputation: userData.authorReputation,
        userPreferences: userData.userPreferences,
        accessToken: '',
        refreshToken: '',
        memberSince: '',
        companyName: '',
        zipPostalCode: '',
        jobTitle: '',
        phoneNumber: '',
      };

      setUserInLocalStorage(userObj);
      setSession(accessToken);
      setUser(userObj);
      setIsSignedIn(true);
      setIsLoading(false);
    } else {
      console.error('Invalid login response: missing accessToken or user id');
    }
  }, []);

  const logout = useCallback(async () => {
    // TRIL-231: flag the logout BEFORE any state change. setIsSignedIn(false)
    // below re-renders the still-mounted private route, re-running App.tsx's
    // guard; the guard reads this ref to skip capturing a spurious `from`.
    // Set synchronously here so it is already true when that re-render runs.
    loggingOutRef.current = true;

    // 1. Redux: single action triggers root-reducer reset across all slices
    dispatch({ type: 'auth/logout' });

    // 2. Apollo: clear all cached query results. clearStore (not resetStore)
    //    because we're navigating away — no active queries should refetch.
    //    Cache clear is best-effort: if Apollo rejects (custom error link,
    //    misconfiguration), the rest of logout must still complete. Logout
    //    cannot half-fail.
    try {
      await apolloClient.clearStore();
    } catch (error) {
      console.error('apolloClient.clearStore() failed during logout:', error);
    }

    // 3. localStorage: tokens + userData
    clearAuthFromLocalStorage();

    // 4. Local context state
    setSession('');
    setUser(null);
    setIsSignedIn(false);
    setIsLoading(false);

    // 5. Route to login
    navigate('/log-in');
  }, [dispatch, navigate]);

  const updateUser = useCallback((userData: Partial<CoreUserDDB>) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      const updatedUser = { ...prevUser, ...userData };
      setUserInLocalStorage(updatedUser);
      return updatedUser;
    });
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshTokenValue = await getTokenFromLocalStorage('refreshToken');
      if (!refreshTokenValue) {
        await logout();
        return false;
      }
      // TRIL-221: call the server's refreshAuthToken mutation. The server
      // rotates both tokens (the old refresh token is deleted from
      // RefreshTokensTable). Write the new pair atomically before flipping
      // isSignedIn so any consumer reading localStorage sees a consistent
      // set.
      const result = await apolloClient.mutate({
        mutation: REFRESH_AUTH_TOKEN_MUTATION,
        variables: { refreshToken: refreshTokenValue },
      });
      const tokens = result.data?.refreshAuthToken;
      if (!tokens?.accessToken || !tokens?.refreshToken) {
        await logout();
        return false;
      }
      window.localStorage.setItem('accessToken', tokens.accessToken);
      window.localStorage.setItem('refreshToken', tokens.refreshToken);
      // TRIL-377: sessionId is stable across rotation — keep it fresh.
      if (tokens.sessionId) {
        window.localStorage.setItem('sessionId', tokens.sessionId);
      }
      const hydratedUser = decodeUserFromToken(tokens.accessToken);
      setSession(tokens.accessToken);
      if (hydratedUser) setUser(hydratedUser);
      setIsSignedIn(true);
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      await logout();
      return false;
    }
  }, [logout]);

  // TRIL-221: mount-time silent refresh. Fires once when readInitialAuthState
  // signaled needsRefresh=true (expired access token + valid refresh token in
  // localStorage). Whatever the outcome (success → tokens rotated and
  // isSignedIn=true; failure → logout already cleared state), setIsLoading
  // (false) so route guards can act. While isLoading=true, App.tsx renders
  // <SimpleBackdrop> so the user never sees an empty <main> during refresh.
  //
  // The refreshFiredRef guard is mandatory because <React.StrictMode> (in
  // main.tsx) double-invokes effects in dev: without the guard, both
  // invocations read the SAME old refresh token from localStorage and fire
  // the mutation twice in parallel. The server deletes the old token on
  // success (one-time rotation), so call #2 then 401s and falls through to
  // logout — the exact bug this feature prevents. The ref is set
  // synchronously, before any await, so the second StrictMode invocation
  // sees true and bails immediately.
  const refreshFiredRef = useRef(false);
  useEffect(() => {
    if (!isLoading) return;
    if (refreshFiredRef.current) return;
    refreshFiredRef.current = true;
    void (async () => {
      await refreshToken();
      setIsLoading(false);
    })();
    // refreshToken is stable via useCallback, but listing it (or its closed-
    // over setters) would risk re-firing if a parent re-renders during the
    // refresh window. This is a true mount-only effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: AuthContextProps = {
    isSignedIn,
    isLoading,
    session,
    isSignedInRef,
    loggingOutRef,
    user,
    login,
    logout,
    updateUser,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
